import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import axios from "axios";
import Plot from "react-plotly.js";
import { cloneDeep, forEach, find } from 'lodash';

import { changeDateRange, toggleMode, handleSuccess, handleErrors } from "../../actions";
import BASE_URL from "../../helpers/base-url";
import authorizeRequest from "../../helpers/authorize-request";
import endpointsExportConfig from '../../constants/endpoints-export-config.js';

// Material UI
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import InsertDriveFile from "@material-ui/icons/InsertDriveFile";
import Close from "@material-ui/icons/Close";
import CameraAlt from "@material-ui/icons/CameraAlt";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DateRange from "@material-ui/icons/DateRange";
import Modal from "@material-ui/core/Modal";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CustomDateRangePicker from "../common/CustomDateRangePicker";
import Tooltip from "@material-ui/core/Tooltip";

//Selectors
import reshapeData from "../../selectors/reshape_data_for_chart";
import reshapeCSVData from "../../selectors/reshape_data_for_csv";

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`
  };
}

const styles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  paper: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4)
  },
  plot: {
    flexGrow: 1,
    width: '100%',
    height: '100%',

    "& .modebar": {
      visibility: "hidden"
    }
  },
  warningMessage: {
    position: 'relative',
    border: `2px solid ${theme.palette.secondary.main}`,
    margin: '0 auto',
    maxWidth: '720px',
    padding: theme.spacing(1),
    paddingRight: theme.spacing(7)
  },
  warningParagraph: {
    margin: 0,
    color: theme.palette.secondary.main
  },
  warningClose: {
    position: 'absolute',
    top: '50%',
    right: theme.spacing(1),
    transform: 'translateY(-50%)'
  }
});

const warningRanges = {
  range100: {
    start: 100,
    end: 25,
    reset: true
  },
  range25: {
    start: 25,
    end: 20,
    closed: false,
  },
  range20: {
    start: 20,
    end: 15,
    closed: false,
  },
  range15: {
    start: 15,
    end: 10,
    closed: false,
  },
  range10: {
    start: 10,
    end: 5,
    closed: false,
  },
  range5: {
    start: 5,
    end: 0,
    closed: false,
  },
  range0: {
    start: 0,
    end: -1,
    closed: false,
  }
};

class OverviewChart extends React.Component {
  state = {
    openDateRangeModal: false,
    openExportModal: false,
    revision: 0,
    sendExportEmail: '',
    showWarningMessage: false,
    warningRanges: cloneDeep(warningRanges)
  };

  componentDidMount() {
    this.graphPlotted = React.createRef();
    const sendExportEmail = localStorage.getItem('sendExportEmail') || '';
    this.setState({ sendExportEmail });
    this.updateWarningMessage();
  }

  componentDidUpdate(prevProps) {
    const { soc } = this.props;
    const { soc: prevSoc } = prevProps;

    if (soc !== prevSoc) {
      this.updateWarningMessage();
    }
  }

  resetWarningMessages(ranges) {
    forEach(ranges, range => !range.reset && (range.closed = false));
  }

  isCurrentRange(range) {
    const { soc } = this.props;
    return soc !== null && range.start >= soc && range.end < soc;
  }

  isBeforeRange(range) {
    const { soc } = this.props;
    return soc !== null && range.end > soc;
  }

  isAfterRange(range) {
    const { soc } = this.props;
    return soc !== null && range.start < soc;
  }

  updateWarningMessage() {
    const { warningRanges } = this.state;
    const workingWarningRanges = cloneDeep(warningRanges);

    // Start from the higher range
    forEach(workingWarningRanges, range => {
      if (this.isCurrentRange(range)) {
        if (range.reset) {
          // If current range & is reset : reset messages
          this.resetWarningMessages(workingWarningRanges);
        } else if (!range.closed) {
          // If current range & has to show warning : if not already closed, show message
          this.setState({ showWarningMessage: true });
        }
      } else if (!range.reset && this.isBeforeRange(range)) {
        // If is higher range, don't show message if battery goes up
        range.closed = true;
      } else if (!range.reset && this.isAfterRange(range)) {
        // If is lower range, reset warning message if battery goes down
        range.closed = false;
      }
    });

    this.setState({ warningRanges: workingWarningRanges });
  }

  handleCloseWarningMessage = () => {
    const workingWarningRanges = cloneDeep(warningRanges);

    find(workingWarningRanges, range => {
      if (this.isCurrentRange(range)) {
        range.closed = true;
        return true;
      }
    });

    this.setState({
      showWarningMessage: false,
      warningRanges: workingWarningRanges
    })
  };

  handleOpen = () => this.setState({ openDateRangeModal: true });
  handleClose = () => this.setState({ openDateRangeModal: false });

  handleCustomDateRangePickerSubmit = ({ startDate, endDate }) => {
    this.props.changeDateRange({ startDate, endDate }, false);
    this.props.mode.realtime && this.props.toggleMode();
    this.handleClose();
  };

  handleOpen2 = () => this.setState({ openExportModal: true });
  handleClose2 = () => this.setState({ openExportModal: false });

  handleCustomDateRangePickerSubmit2 = async ({ startDate, endDate, emailValue: email }) => {
    const { handleSuccess, handleErrors } = this.props;

    localStorage.setItem('sendExportEmail', email);
    this.setState({ sendExportEmail: email });

    try {
      const { data: result } = await axios.post(`${BASE_URL}/api/export`, {
        startDate: startDate.format(),
        endDate: endDate.format(),
        email,
        endpoints: endpointsExportConfig
      }, authorizeRequest());

      if (result === "Export in progress") {
        handleSuccess({ message: result, open: true });
      } else {
        handleErrors({ message: result, open: true });
      }

    } catch (error) {
      handleErrors({ message: error.message, open: true });
    }

    this.handleClose2();
  };

  downloadGraph = fileName => {
    this.graphPlotted.current.el.querySelector(".modebar-group a").click();
  };

  onUpdate = event => {
    const { revision } = this.state;

    if (event.layout.yaxis2.autorange) {

      // TODO remove this hack - don't mutate event value
      // But needed until Plotly mutate his params on zoom out (double click on graph) \o/
      event.layout.autosize = false;
      event.layout.yaxis2.autorange = false;
      event.layout.yaxis2.range = [0, 300];

      // Apply layout values here
      this.graphPlotted.current.updatePlotly();

      // Force sync react-plotly
      this.setState({
        revision: revision + 1
      });
    }
  };

  render() {
    const {
      classes,
      reshapedChartData: {
        data,
        layout
      },
      dateRange: {
        startDate,
        endDate
      },
      soc
    } = this.props;
    const displayStart = moment(startDate).format("MMM Do YYYY");
    const displayEnd = moment(endDate).format("MMM Do YYYY");
    const {
      revision,
      sendExportEmail,
      openDateRangeModal,
      openExportModal,
      showWarningMessage
    } = this.state;

    return (
      <Paper className={classes.root}>
        <Modal
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          open={openDateRangeModal}
          onClose={this.handleClose}
        >
          <div style={getModalStyle()} className={classes.paper}>
            <CustomDateRangePicker
              onSubmit={this.handleCustomDateRangePickerSubmit}
              actionText="Filter Dates"
            />
          </div>
        </Modal>

        <Modal
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          open={openExportModal}
          onClose={this.handleClose2}
        >
          <div style={getModalStyle()} className={classes.paper}>
            <CustomDateRangePicker
              onSubmit={this.handleCustomDateRangePickerSubmit2}
              twoYearsMode={true}
              emailValue={sendExportEmail}
              actionText="Export to CSV"
            />
          </div>
        </Modal>
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <Typography
              gutterBottom
              variant="h6"
              inline="true"
              align="left"
              style={{ marginLeft: 20 }}
            >
              {`${displayStart} - ${displayEnd}`}
              {this.props.mode.loading && (
                <CircularProgress
                  disableShrink
                  size={20}
                  style={{ marginLeft: 20 }}
                />
              )}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <div style={{ textAlign: " right" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={this.props.mode.realtime}
                    color="secondary"
                    onChange={this.props.toggleMode}
                  />
                }
                label=" Realtime Mode"
              />
              <Tooltip title=" Change Date Range">
                <IconButton
                  aria-label=" Select New Date Range"
                  onClick={this.handleOpen}
                >
                  <DateRange/>
                </IconButton>
              </Tooltip>

              {this.props.reshapedCSVData.length ? (
                <Tooltip title=" Export to CSV">

                  <IconButton
                    aria-label=" Export to CSV"
                    onClick={this.handleOpen2}
                  >
                    <InsertDriveFile/>
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title=" Take screenshot">
                <IconButton
                  aria-label=" Take Screenshot"
                  onClick={this.downloadGraph}
                >
                  <CameraAlt/>
                </IconButton>
              </Tooltip>
            </div>
          </Grid>
        </Grid>
        {showWarningMessage ? (
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <div className={classes.warningMessage}>
                <p className={classes.warningParagraph}><strong>Achtung:</strong> Die Batterie erreicht einen niedrigen
                  Ladezustand. Derzeit ist die Batterie noch <strong>{soc}%</strong> geladen.</p>
                <p className={classes.warningParagraph}>Wir raten, ab jetzt ein Lastmanagement durch Abschalten oder
                  Herunterfahren möglicher Verbraucher auf ihrem Standort.</p>
                <IconButton
                  aria-label="Export to CSV"
                  onClick={this.handleCloseWarningMessage}
                  className={classes.warningClose}
                >
                  <Close/>
                </IconButton>
              </div>
            </Grid>
          </Grid>
        ) : null}
        <Plot
          className={classes.plot}
          useResizeHandler={true}
          data={data}
          layout={layout}
          config={this.config}
          ref={this.graphPlotted}
          revision={revision}
          onUpdate={this.onUpdate}
        />
      </Paper>
    );
  }
}

OverviewChart.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  return {
    dateRange: state.dateRange,
    mode: state.mode,
    reshapedChartData: reshapeData(state),
    reshapedCSVData: reshapeCSVData(state),
    soc: state.siteData.soc
  };
};
export default withStyles(styles)(
  connect(
    mapStateToProps,
    {
      changeDateRange,
      toggleMode,
      handleSuccess,
      handleErrors
    }
  )(OverviewChart)
);



// WEBPACK FOOTER //
// ./src/components/charts/OverviewChart.js